import { useState, useCallback } from 'react';
import { useHttp } from './useHttp';
import { useAuth } from './useAuth';
import type {
  RaffleParticipant,
  RaffleTicket,
  PaginationMeta,
  PaginatedResponse,
} from 'src/types/raffle';

interface UseRaffleParticipantsReturn {
  participants: RaffleParticipant[];
  participantsMeta: PaginationMeta | null;
  transactions: RaffleTicket[];
  transactionsMeta: PaginationMeta | null;
  isLoadingParticipants: boolean;
  isLoadingTransactions: boolean;
  fetchParticipants: (raffleId: string, page?: number, limit?: number) => void;
  fetchTransactions: (raffleId: string, page?: number, limit?: number) => void;
}

export function useRaffleParticipants(): UseRaffleParticipantsReturn {
  const { sendRequest: sendParticipantsReq } = useHttp();
  const { sendRequest: sendTransactionsReq } = useHttp();
  const { accessToken } = useAuth();

  const [participants, setParticipants] = useState<RaffleParticipant[]>([]);
  const [participantsMeta, setParticipantsMeta] = useState<PaginationMeta | null>(null);
  const [isLoadingParticipants, setIsLoadingParticipants] = useState(false);

  const [transactions, setTransactions] = useState<RaffleTicket[]>([]);
  const [transactionsMeta, setTransactionsMeta] = useState<PaginationMeta | null>(null);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);

  const fetchParticipants = useCallback(
    (raffleId: string, page = 1, limit = 20) => {
      setIsLoadingParticipants(true);
      sendParticipantsReq(
        {
          url: `raffles/${raffleId}/participants?page=${page}&limit=${limit}`,
          accessToken: accessToken || undefined,
        },
        (response: unknown) => {
          const res = response as PaginatedResponse<RaffleParticipant>;
          setParticipants(res.data);
          setParticipantsMeta(res.meta);
          setIsLoadingParticipants(false);
        },
      );
    },
    [sendParticipantsReq, accessToken],
  );

  const fetchTransactions = useCallback(
    (raffleId: string, page = 1, limit = 20) => {
      setIsLoadingTransactions(true);
      sendTransactionsReq(
        {
          url: `raffles/${raffleId}/tickets?page=${page}&limit=${limit}`,
          accessToken: accessToken || undefined,
        },
        (response: unknown) => {
          const res = response as PaginatedResponse<RaffleTicket>;
          setTransactions(res.data);
          setTransactionsMeta(res.meta);
          setIsLoadingTransactions(false);
        },
      );
    },
    [sendTransactionsReq, accessToken],
  );

  return {
    participants,
    participantsMeta,
    transactions,
    transactionsMeta,
    isLoadingParticipants,
    isLoadingTransactions,
    fetchParticipants,
    fetchTransactions,
  };
}
